/**
 * Group Service
 * Handles group management API operations.
 */

let API_URL = ""

async function loadApiUrl(): Promise<string> {
  if (API_URL) return API_URL
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  const baseUrl = config.feedbackApiUrl
  if (!baseUrl) throw new Error("API URL not found")
  API_URL = `${baseUrl.replace(/\/?$/, "/")}`
  return API_URL
}

async function fetchWithAuth(
  url: string,
  idToken: string,
  options: RequestInit = {}
) {
  const response = await fetch(url, {
    ...options,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...options.headers,
    },
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error?.message || `HTTP error! status: ${response.status}`
    )
  }
  return response.json()
}

// --- Types ---

export interface Group {
  groupId: string
  name: string
  description: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface GroupMember {
  groupId: string
  userId: string
  userEmail: string
  addedBy: string
  addedAt: string
}

// --- Group CRUD ---

export async function listGroups(idToken: string): Promise<Group[]> {
  const url = await loadApiUrl()
  const data = await fetchWithAuth(`${url}admin/groups`, idToken)
  return data.groups || []
}

export async function createGroup(
  idToken: string,
  name: string,
  description: string
): Promise<Group> {
  const url = await loadApiUrl()
  const data = await fetchWithAuth(`${url}admin/groups`, idToken, {
    method: "POST",
    body: JSON.stringify({ name, description }),
  })
  return data.group
}

export async function updateGroup(
  idToken: string,
  groupId: string,
  name: string,
  description: string
): Promise<Group> {
  const url = await loadApiUrl()
  const data = await fetchWithAuth(`${url}admin/groups/${groupId}`, idToken, {
    method: "PUT",
    body: JSON.stringify({ name, description }),
  })
  return data.group
}

export async function deleteGroup(
  idToken: string,
  groupId: string
): Promise<void> {
  const url = await loadApiUrl()
  await fetchWithAuth(`${url}admin/groups/${groupId}`, idToken, {
    method: "DELETE",
  })
}

// --- Member Management ---

export async function listGroupMembers(
  idToken: string,
  groupId: string
): Promise<GroupMember[]> {
  const url = await loadApiUrl()
  const data = await fetchWithAuth(
    `${url}admin/groups/${groupId}/members`,
    idToken
  )
  return data.members || []
}

export async function addGroupMember(
  idToken: string,
  groupId: string,
  userId: string,
  userEmail: string
): Promise<GroupMember> {
  const url = await loadApiUrl()
  const data = await fetchWithAuth(
    `${url}admin/groups/${groupId}/members`,
    idToken,
    {
      method: "POST",
      body: JSON.stringify({ userId, userEmail }),
    }
  )
  return data.member
}

export async function removeGroupMember(
  idToken: string,
  groupId: string,
  userId: string
): Promise<void> {
  const url = await loadApiUrl()
  await fetchWithAuth(
    `${url}admin/groups/${groupId}/members/${userId}`,
    idToken,
    { method: "DELETE" }
  )
}

export async function listUserGroups(
  idToken: string,
  userId: string
): Promise<Group[]> {
  const url = await loadApiUrl()
  const data = await fetchWithAuth(
    `${url}admin/users/${userId}/groups`,
    idToken
  )
  return data.groups || []
}
